import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal as BootstrapModal, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../../../common/errorMessages'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import {
  createAdditionalObjectsValidatorRegistry,
  type AdditionalObjectsValidator,
  useAdditionalObjectsContactLinking,
} from '../../../../../common/useAdditionalObjectsContactLinking'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { Modal } from '../../../../../components/Modal/Modal'
import { SelectDropdown, SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { ExternalUserServiceIntegration, User, UserCreateUpdateIntegrations } from '../../../../../models/User'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useGetExternalUserByIdQuery, useSaveExternalUserByIdMutation } from '../../../../../store/externalUser/externalUser'
import { useAppSelector } from '../../../../../store/hooks'
import { useGetIntegrationUsersQuery, useLazyGetIntegrationUsersQuery } from '../../../../../store/integrations/integrations'
import { getContactOptions } from '../../../../../views/MeetingSummary/CRM/utils'
import { getContactDropdownComponent } from '../../../../../views/MeetingSummary/ContactDropdownItems'
import { getUserName } from '../../../../../views/utils'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]

export interface LinkContactModalProps {
  show: boolean
  onSave: () => void
  onCancel: () => void
  serviceId: IntegrationServiceId
  user: User
}

type FormValues = {
  integrations: UserCreateUpdateIntegrations[]
}

export const LinkContactModal: React.FC<LinkContactModalProps> = ({ show, onSave, onCancel, serviceId, user }) => {
  const accountId = useAppSelector(selectAccountId)
  const userId = user.id

  const defaultValues = {
    integrations: [],
  }
  const validatorRegistry = useMemo(() => createAdditionalObjectsValidatorRegistry(), [])
  const { run: runAdditionalObjectsValidators, register: registerAdditionalObjectsValidator } = validatorRegistry
  const [saveUserById, { isLoading: isUpdating, isSuccess }] = useSaveExternalUserByIdMutation()

  useEffect(() => {
    if (isSuccess) {
      onSave()
    }
  }, [isSuccess, onSave])

  const form = useForm<FormValues>({ defaultValues })
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form

  const handleSave = async (data: FormValues) => {
    const { isValid, integrations } = await runAdditionalObjectsValidators(data.integrations)
    if (!isValid) {
      return
    }

    saveUserById({
      accountId,
      userId,
      userData: {
        firstName: user.firstName,
        lastName: user.lastName,
        name: getUserName(user.firstName, user.lastName) || undefined,
        email: user.email,
        referenceUserId: null,
        owner: !user.owner ? null : user.owner,
        integrations,
      },
    })
  }

  const integrationDisplayName = getIntegrationDisplayName(serviceId)

  const body = (
    <Container>
      <Row className='mb-4'>
        Link Contact to {integrationDisplayName} to pull their client data into the Meeting Prep. Rerun the prep after linking to include the updated
        information.
      </Row>
      <Row>
        <FormProvider {...form}>
          <LinkContactModalForm serviceId={serviceId} user={user} registerAdditionalObjectsValidator={registerAdditionalObjectsValidator} />
        </FormProvider>
      </Row>
    </Container>
  )

  const header = (
    <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-start'>Link Contact To {integrationDisplayName}</BootstrapModal.Title>
  )

  return (
    <Modal
      show={show}
      onHide={onCancel}
      header={header}
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionTitle={'Link Contact'}
      footerPrimaryActionLoading={isUpdating || isSubmitting}
      centered={true}
      bodyClassName='mt-3'
      headerBodySeparator={true}
    />
  )
}

interface LinkContactModalFormProps {
  serviceId: IntegrationServiceId
  user: User
  registerAdditionalObjectsValidator: (validator: AdditionalObjectsValidator) => () => void
}

const LinkContactModalForm: React.FC<LinkContactModalFormProps> = ({ serviceId, user, registerAdditionalObjectsValidator }) => {
  const accountId = useAppSelector(selectAccountId)
  const userId = user.id
  const { data: userData, isFetching: isFetchingUserData } = useGetExternalUserByIdQuery(
    {
      accountId,
      userId,
    },
    { refetchOnMountOrArgChange: true },
  )

  const { setValue } = useFormContext<FormValues>()

  useEffect(() => {
    if (!userData) {
      return
    }
    if (userData) {
      setValue('integrations', userData.data.integrations ?? [])
    }
  }, [userData, setValue])

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {isFetchingUserData ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <IntegrationSelectorRow key={serviceId} serviceId={serviceId} user={user} registerAdditionalObjectsValidator={registerAdditionalObjectsValidator} />
        </>
      )}
    </Container>
  )
}

const CONTACT_MAPPING_LABEL_MAPPING: { [key: string]: string } = {
  contact: 'Contact',
  account: 'Account',
}

interface IntegrationSelectorRowProps {
  serviceId: IntegrationServiceId
  user: User
  registerAdditionalObjectsValidator: (validator: AdditionalObjectsValidator) => () => void
}

const IntegrationSelectorRow: React.FC<IntegrationSelectorRowProps> = ({ serviceId, user, registerAdditionalObjectsValidator }) => {
  const accountId = useAppSelector(selectAccountId)

  const {
    data: contacts,
    isFetching: isContactsFetching,
    isError: isContactsError,
    error: contactsError,
  } = useGetIntegrationUsersQuery({ accountId, connector: serviceId })
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [triggerGetContacts, { isError: isTriggerGetContactsError, error: triggerContactsError }] = useLazyGetIntegrationUsersQuery()

  const { control, watch } = useFormContext()
  const integrations = watch('integrations') as ExternalUserServiceIntegration[]
  const { append: appendIntegration, update: updateIntegration } = useFieldArray({
    control,
    name: 'integrations',
  })

  const integration = user.integrations?.find((a) => a.serviceId === serviceId)
  const externalId = integration?.externalId ?? ''

  const [searchContactOptions, setSearchContactOptions] = useState<SelectOption[] | null>(null)
  const [selectedContactOption, setSelectedContactOption] = useState<SelectOption | null>(null)
  const [loading, setLoading] = useState<boolean>(!!externalId)

  const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
  const contactMapping = serviceIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT] ?? 'contact'
  const contactLabel =
    typeof contactMapping === 'string'
      ? CONTACT_MAPPING_LABEL_MAPPING[contactMapping]
      : (contactMapping.displayName ?? CONTACT_MAPPING_LABEL_MAPPING[contactMapping.objectName] ?? 'Contact')

  const getContactByExternalId = useCallback(
    async (externalId: string) => {
      try {
        const { data } = await triggerGetContacts({
          accountId,
          connector: serviceId,
          filters: { externalIds: [externalId] },
        }).unwrap()
        return data[0]
      } catch (e) {
        console.error(e)
      }
    },
    [triggerGetContacts, accountId, serviceId],
  )

  const initialContactOptions = useMemo(() => {
    if (contacts && contacts.data) {
      return getContactOptions(contacts.data, serviceId)
    }
    return []
  }, [contacts, serviceId])

  useEffect(() => {
    if (!externalId) {
      return
    }
    getContactByExternalId(externalId)
      .then((contact) => {
        if (!contact) {
          return
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [serviceId, externalId, getContactByExternalId])

  const onSearchInputChange = async (searchInput: string) => {
    try {
      const contactsData = await triggerGetContacts({
        accountId,
        connector: serviceId,
        filters: { name: searchInput },
      }).unwrap()
      if (contactsData && contactsData.data) {
        setSearchContactOptions(getContactOptions(contactsData.data, serviceId))
      }
    } catch (e) {
      console.error(e)
    }
  }

  const { isAdditionalObjectsContactLinking, identifierValue, identifierError, isLoadingIdentifier, handleIdentifierChange } =
    useAdditionalObjectsContactLinking({
      serviceId,
      initialExternalId: externalId,
      registerAdditionalObjectsValidator,
    })

  if (
    (isContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(contactsError).match(ignoredMessage))) ||
    (isTriggerGetContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(triggerContactsError).match(ignoredMessage)))
  ) {
    return (
      <>
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid, please reconnect your integration:</Col>
          </Row>
        </Container>
      </>
    )
  }

  const handleOnOptionSelected = async (option: SelectOption) => {
    setLoading(true)
    const contact = await getContactByExternalId(option.id)
    if (!contact || !contact.integrations) {
      setLoading(false)
      return
    }
    const integrationIndex = integrations.findIndex((e) => e.serviceId === serviceId)
    if (integrationIndex > -1) {
      updateIntegration(integrationIndex, contact.integrations[0])
    } else {
      appendIntegration(contact.integrations[0])
    }
    setSelectedContactOption(option)
    setLoading(false)
  }

  const serviceIdDisplayName = getIntegrationDisplayName(serviceId)

  return (
    <Row>
      <Col>
        <Form.Label>Link {user.name} to</Form.Label>
        {isAdditionalObjectsContactLinking ? (
          isAccountFetching || loading || isLoadingIdentifier ? (
            <div className='d-flex justify-content-center'>
              <Spinner />
            </div>
          ) : (
            <div className='d-flex align-items-center zocks-gap-0.5'>
              <div className='flex-grow-1'>
                <Form.Control
                  type='text'
                  value={identifierValue}
                  placeholder='Enter external identifier…'
                  onChange={(event) => {
                    handleIdentifierChange(event.target.value)
                  }}
                  isInvalid={!!identifierError}
                />
                {identifierError && <FormFeedback>{identifierError}</FormFeedback>}
              </div>
            </div>
          )
        ) : isContactsFetching || isAccountFetching || loading || !contacts ? (
          <div className='d-flex justify-content-center'>
            <Spinner />
          </div>
        ) : (
          <div className='d-flex align-items-center zocks-gap-0.5'>
            <SelectDropdown
              initialSelectedOption={selectedContactOption}
              options={searchContactOptions ?? initialContactOptions}
              className='py-2 zocks-px-0.75'
              onOptionSelected={handleOnOptionSelected}
              DropDownItemOptionComponent={getContactDropdownComponent(serviceId)}
              searchable={true}
              onSearchInputChange={onSearchInputChange}
              placeholder={`Select ${serviceIdDisplayName} ${contactLabel}`}
            />
          </div>
        )}
      </Col>
    </Row>
  )
}
